/**
 * FriendCard Component
 * 
 * Displays a friend's information including portrait and name.
 * Used for displaying friends in lists and cards.
 */

import React, { useState, useEffect } from 'react';
import { UserId } from 'coop-adventure-engine';
import { CharacterPortrait } from './CharacterPortrait';
import apiService from '../../services/api';

export interface FriendCardProps {
  /**
   * User ID of the friend (required)
   */
  friendId: UserId | string;

  /**
   * Optional click handler
   */
  onClick?: () => void;

  /**
   * Whether to show action buttons (for friend requests: accept/reject)
   */
  showActions?: boolean;

  /**
   * Optional accept handler
   */
  onAccept?: () => void;

  /**
   * Optional reject handler
   */
  onReject?: () => void;

  /**
   * Additional CSS classes
   */
  className?: string;

  /**
   * Custom styles
   */
  style?: React.CSSProperties;

  /**
   * If true, renders without container styling (for use inside buttons)
   */
  noContainer?: boolean;
}

/**
 * FriendCard component for displaying friend information
 */
export const FriendCard: React.FC<FriendCardProps> = ({
  friendId,
  onClick,
  showActions = false,
  onAccept,
  onReject,
  className = '',
  style,
  noContainer = false
}) => {
  const [userName, setUserName] = useState<string>('Loading...');
  const [portraitId, setPortraitId] = useState<string | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadUserInfo = async () => {
      setIsLoading(true);
      setError(null);
      
      try {
        // Fetch user name and portrait from API
        const userInfo = await apiService.getUserInfo(friendId);
        
        // Use playerName (from user_player_name variable) if available, otherwise fall back to username
        setUserName(userInfo.playerName || userInfo.username || `User ${friendId.substring(0, 8)}`);
        setPortraitId(userInfo.portraitId || undefined);
      } catch (err) {
        console.error(`Failed to load friend info for ${friendId}:`, err);
        setError(err instanceof Error ? err.message : 'Failed to load friend info');
        setUserName('Unknown User');
        setPortraitId(undefined);
      } finally {
        setIsLoading(false);
      }
    };

    if (friendId) {
      loadUserInfo();
    }
  }, [friendId]);

  const cardClasses = `
    friend-card
    ${onClick ? 'cursor-pointer hover:opacity-90 transition-opacity' : ''}
    ${className}
  `.trim();

  if (error && !isLoading) {
    return (
      <div
        data-testid={`friend-card-${friendId}-error`}
        className={`friend-card error ${className}`}
        style={{
          backgroundColor: 'rgba(0, 0, 0, 0.3)',
          border: '1px solid rgba(255, 0, 0, 0.3)',
          borderRadius: '0.75rem',
          padding: '1rem',
          ...style
        }}
      >
        <div className="text-center py-4">
          <p className="text-red-400 text-sm">{error}</p>
        </div>
      </div>
    );
  }

  const content = (
    <div className="flex items-center w-full">
      {/* Friend Portrait */}
      <div className="flex-shrink-0" style={{ marginRight: '1rem' }}>
        <CharacterPortrait
          portraitId={portraitId}
          variant="thumbnail"
          alt={userName}
        />
      </div>

      {/* Friend Name - centered vertically */}
      <div className="flex-1 flex items-center">
        <span
          data-testid={`friend-name-${friendId}`}
          className={`text-base${noContainer ? '' : ' text-white'}`}
        >
          {isLoading ? 'Loading...' : userName}
        </span>
      </div>

      {/* Action Buttons */}
      {showActions && (onAccept || onReject) && (
        <div className="flex gap-2 flex-shrink-0">
          {onAccept && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onAccept();
              }}
              className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors"
              data-testid={`friend-accept-${friendId}`}
            >
              Accept
            </button>
          )}
          {onReject && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onReject();
              }}
              className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors"
              data-testid={`friend-reject-${friendId}`}
            >
              Reject
            </button>
          )}
        </div>
      )}
    </div>
  );

  if (noContainer) {
    return (
      <div
        data-testid={`friend-card-${friendId}`}
        className={cardClasses}
        style={{
          padding: '0.75rem 1rem',
          width: '100%',
          ...style
        }}
        onClick={onClick}
      >
        {content}
      </div>
    );
  }

  return (
    <div
      data-testid={`friend-card-${friendId}`}
      className={cardClasses}
      style={{
        backgroundColor: 'rgba(0, 0, 0, 0.3)',
        border: '1px solid rgba(255, 255, 255, 0.2)',
        borderRadius: '0.75rem',
        padding: '1rem',
        backdropFilter: 'blur(12px)',
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.2)',
        ...style
      }}
      onClick={onClick}
    >
      {content}
    </div>
  );
};

export default FriendCard;

